/*
 * Per-rule copy for the Task Generation card. Pure and tested because
 * index.jsx has no test harness.
 */

import moment from 'moment-timezone';
import { format_duration_minutes } from '../../util_components/instant_alert/instant_timer';
import { DEFAULT_LEAD_MINUTES } from './gen_config';
import type { GenRule } from './gen_config';

// Server-sent display fields the rule rows read on top of GenRule.
export type CardRule = GenRule & {
  label?: string;
  anchor_label?: string;
  bucket?: string;
  no_book_day_zero_override?: boolean;
};

const is_blank = (v: unknown): boolean => v === '' || v === null || v === undefined;

// For the no-join rules the server computes dueAt = session start + delay, so
// the value is not a wait after a trigger.
const SESSION_START_DELAY_TYPES: ReadonlyArray<string | null | undefined> = [
  'PRE_SESSION_NO_JOIN',
  'REMINDER_LINK_NO_JOIN'
];

export const delay_label = (taskType: string | null | undefined): string => {
  if (SESSION_START_DELAY_TYPES.includes(taskType)) return 'Minutes after session start';
  if (taskType === 'THREE_MONTH_PLAN_OPENED') return 'Minutes after plan opened';
  if (taskType === 'REMINDER_REPLY') return 'Reply window (minutes)';
  return 'Delay (minutes)';
};

export const delay_title = (taskType: string | null | undefined): string => {
  if (SESSION_START_DELAY_TYPES.includes(taskType)) {
    return 'How many minutes after the session starts to check whether the member joined. Blank = the server default.';
  }
  if (taskType === 'REMINDER_REPLY') {
    return 'How long to wait for a reply to the reminder before creating the task. Blank = the server default.';
  }
  if (taskType === 'THREE_MONTH_PLAN_OPENED') {
    return 'How many minutes after the lead opens their plan to create the call. Blank = 30.';
  }
  return 'How long to wait after the trigger before re-checking whether the member got through on their own. Blank = the server default.';
};

// THREE_MONTH_PLAN_OPENED dedupes once per plan, so goserver ignores its dedup window.
export const has_dedup_window = (taskType: string | null | undefined): boolean =>
  taskType !== 'THREE_MONTH_PLAN_OPENED';

// Mirrors goserver: a blank instant expiry enforces 12 h on every rule.
export const BLANK_EXPIRY_MINUTES = 720;

export const EXPIRY_PLACEHOLDER = String(BLANK_EXPIRY_MINUTES);

export const EXPIRY_TITLE =
  'An instant task nobody touches within this many minutes is auto-completed. Leave blank for 12 h.';

// '' when the server sent no enabled_at or an unparseable one.
export const enabled_since_label = (enabledAt: unknown): string => {
  if (!enabledAt || typeof enabledAt !== 'string') return '';
  const m = moment(enabledAt, moment.ISO_8601, true);
  if (!m.isValid()) return '';
  return `Enabled since ${m.tz('Asia/Kolkata').format('D MMM YYYY')}`;
};

// Row trigger line: the anchor for event rules, the picked days for day-offset rules.
export const rule_trigger_text = (rule: CardRule): string => {
  const anchor = rule.anchor_label || '';
  if (rule.enabled_only || rule.has_days === false) return anchor;
  const days = rule.days || [];
  if (!days.length) return `Pick the days (${anchor})`;
  return `Day ${days.join(', ')} — ${anchor}`;
};

const delay_part = (rule: CardRule, minutes: number): string => {
  if (SESSION_START_DELAY_TYPES.includes(rule.task_type)) return `${minutes} min after start`;
  if (rule.task_type === 'THREE_MONTH_PLAN_OPENED') return `${minutes} min after open`;
  if (rule.task_type === 'REMINDER_REPLY') return `reply window ${minutes} min`;
  return `delay ${minutes} min`;
};

/*
 * Collapsed-row timing summary from the same values the edit panel shows.
 * Blank delay/grace/lookback mean "server default", which the client does not know, so they are left out.
 */
export const rule_timing_summary = (rule: CardRule): string => {
  const parts: string[] = [];
  if (!rule.enabled_only) {
    parts.push(
      `re-scan ${rule.refresh_rate_hours ?? 24} h`,
      `due ${rule.due_offset_hours ?? 24} h`
    );
  }
  if (rule.is_instant) {
    parts.push(
      `expires ${format_duration_minutes(Number(rule.expiry_minutes) || BLANK_EXPIRY_MINUTES)}`
    );
    if (has_dedup_window(rule.task_type) && !is_blank(rule.dedup_window_hours)) {
      parts.push(`dedup ${rule.dedup_window_hours} h`);
    }
  }
  if (rule.has_grace && !is_blank(rule.grace_minutes))
    parts.push(`grace ${rule.grace_minutes} min`);
  if (rule.has_delay && Number(rule.delay_minutes) > 0) {
    parts.push(delay_part(rule, Number(rule.delay_minutes)));
  }
  if (rule.has_lead) {
    const lead = is_blank(rule.lead_minutes) ? DEFAULT_LEAD_MINUTES : rule.lead_minutes;
    parts.push(`${lead} min before session`);
  }
  if (rule.has_lookback && Number(rule.lookback_hours) > 0) {
    parts.push(`lookback ${rule.lookback_hours} h`);
  }
  if (!parts.length) return '—';
  const text = parts.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Rules section header, e.g. "36 rules · 31 on".
export const rules_count_label = (rules: ReadonlyArray<Pick<GenRule, 'enabled'>>): string => {
  const on = rules.filter((r) => r.enabled).length;
  return `${rules.length} ${rules.length === 1 ? 'rule' : 'rules'} · ${on} on`;
};
